import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
// i18N
import intl from 'i18n/intl';
import type { PinCodeMode } from 'shared/preferences-constants';
import {
  PrefGeneralSettingsFlushCacheLoading,
  PrefGeneralSettingsInnerSettings,
  PrefGeneralSettingsLoading,
  flushOrganizationCache,
  getPreferencesGeneralSettings,
  updatePreferencesGeneralSettings,
} from 'slices/appsPrefGeneralSettings';
import { useAppDispatch } from 'slices/store';
import {
  Alert,
  Button,
  ConfirmationPopup,
  DefaultContentLayout,
  TEInput,
  Tooltip,
} from '@timeedit/ui-components';
import { Form, Radio, Typography } from '@timeedit/ui-components/antd';
import LoadingWrapper from '../../../components/LoadingWrapper';

const language: any = intl.messages;

const pinCodeModeTranslateMapping: Record<
  PinCodeMode,
  { optionLabel: string; tooltipLabel: string }
> = {
  PIN_CODE_GENERATE: {
    optionLabel: 'preferences_settings_pin_code_generate',
    tooltipLabel: 'preferences_settings_pin_code_generate_tooltip_expl',
  },
  PIN_CODE_AUTH_SIGNAL: {
    optionLabel: 'preferences_settings_pin_code_auth_signal',
    tooltipLabel: 'preferences_settings_pin_code_auth_signal_tooltip_expl',
  },
};

const PreferencesGeneralSettings = () => {
  const dispatch = useAppDispatch();

  useEffect(() => {
    dispatch(getPreferencesGeneralSettings());
  }, [dispatch]);

  const isLoading = useSelector(PrefGeneralSettingsLoading);
  const innerSettings = useSelector(PrefGeneralSettingsInnerSettings);
  const flushCacheLoading = useSelector(PrefGeneralSettingsFlushCacheLoading);

  const [innerSettingsState, setInnerSettingsState] = useState(innerSettings);
  useEffect(() => {
    setInnerSettingsState(innerSettings);
  }, [innerSettings]);

  const saveSettings = () => {
    dispatch(updatePreferencesGeneralSettings(innerSettingsState));
  };

  return (
    <DefaultContentLayout
      title={language.preferences_settings}
      subtitle={language.general_settings}
      actions={
        <>
          <Button type="primary" size="small" onClick={saveSettings}>
            {language.save_changes}
          </Button>
          <ConfirmationPopup
            message={language.clear_cache_confirm_message}
            onConfirm={() => dispatch(flushOrganizationCache())}
            okText={language.yes}
            cancelText={language.no}
          >
            <Button size="small" loading={flushCacheLoading}>
              {language.clear_cache}
            </Button>
          </ConfirmationPopup>
        </>
      }
    >
      <LoadingWrapper isLoading={isLoading}>
        <div>
          <h3 className="text--secondary">
            {language.preferences_settings_pin_code_head_line}
          </h3>
          <div className="content">
            <Form layout="vertical" style={{ padding: '15px' }}>
              <Form.Item>
                <Radio.Group
                  value={innerSettingsState.pinCodeMode}
                  onChange={(event) => {
                    setInnerSettingsState((prevState) => {
                      return {
                        ...prevState,
                        pinCodeMode: event.target.value,
                      };
                    });
                  }}
                  style={{ fontSize: 'revert' }}
                >
                  {Object.entries(pinCodeModeTranslateMapping).map(
                    ([key, { optionLabel, tooltipLabel }]) => (
                      <TEInput.Radio
                        key={key}
                        value={key}
                        label={language[optionLabel]}
                        tooltip={language[tooltipLabel]}
                      />
                    )
                  )}
                </Radio.Group>
              </Form.Item>
            </Form>
          </div>

          <Alert
            type="info"
            title={language.preferences_settings_pin_code_info_box_head_line}
            description={
              <div>
                <p>{language.preferences_settings_pin_code_head_prim_info}</p>
                <p>
                  <b>
                    {language.preferences_settings_pin_code_head_prim_notes}
                  </b>
                </p>
                <ol>
                  <li>
                    {
                      language.preferences_settings_pin_code_head_prim_note_new_rec
                    }
                  </li>
                </ol>
              </div>
            }
          />
        </div>
      </LoadingWrapper>
    </DefaultContentLayout>
  );
};

export default PreferencesGeneralSettings;
